import { Card } from "@/src/components/wrappers/Card";
import { Content } from "@/src/components/wrappers/Content";
import { ActionButton, BackButton, WithActionBar } from "@/src/components/wrappers/WithActionBar";
import { EditOperatorForm } from "./EditOperatorForm/EditOperatorForm";
import { ChevronRight } from "lucide-react";
import { useLoaderData, useParams } from "react-router-dom";
import { BoardingInfo, Operator, YachtLocation } from "./model";
import { fetch } from "@/src/lib/fetch";
import { useEffect, useMemo, useState } from "react";
import { OperatorForm } from "./EditOperatorForm/operator.schema";
import { useFetch } from "@/src/lib/useFetch";
import { useToast } from "@/src/components/ui/use-toast";
import { QueryClient } from "@tanstack/react-query";
import { useOperatorDataQuery } from "../../lib/operatorByIdQuery";

export function EditOperator() {
  const operatorInitialData = useLoaderData() as Operator;
  const [operator, setOperator] = useState(operatorInitialData);
  const { id: operatorId } = useParams();

  const { data: operatorData } = useOperatorDataQuery(operatorId!) as {
    data: Operator;
  };

  useEffect(() => {
    operatorData && setOperator(operatorData);
  }, [operatorData]);

  const { fetch, isFetching } = useFetch();
  const toast = useToast();

  const [operatorForm, locations]: [OperatorForm, any[]] = useMemo(() => {
    const customBoardingInfo = operator?.boardingInfos
      ?.filter((bi) => !operator.locations?.some((l) => l.id === bi.location.id))
      ?.map((bi) => ({
        ...bi.location,
      }));
    const boardingInfos = [...operator.locations, ...customBoardingInfo];
    return [
      {
        operator: {
          name: operator.name,
          contactPerson: operator.contactPerson,
          email: operator.email,
          email2: operator.email2,
          phone: operator.phone,
          address: operator.address,
          website: operator.website,
          rating: operator.rating,
          ratingAnchor: operator.ratingAnchor,
          ratingHelm: operator.ratingHelm,
        },
        labels: operator.labels,
        visibility: {
          ...operator.visibility,
        },
        transferDetails: operator.transferDetails,
        operatorPayments: {
          commission: { value: operator.commission, type: "percentage" },
          customDiscount: operator.customDiscount,
          paymentSchedule: operator.paymentSchedule,
        },
        termsAndConditions: operator.termsAndConditions,
        goodToKnow: operator.goodToKnow,
        boardingInformations: makeBoardingInfo(boardingInfos, operator.boardingInfos),
        notes: operator.notes,
      },
      boardingInfos,
    ];
  }, [operator]);

  const putOperator = async (data: OperatorForm) => {
    if (!isFetching) {
      try {
        await fetch(`/api/operators/operator/${operator.id}`, "PUT", data);
        toast.toast({
          title: "Operator saved",
          description: "The operator has been saved",
          variant: "default",
        });
      } catch (e: any) {
        console.error(e);
        toast.toast({
          title: "Failed to save operator",
          description: e.message || "An error occurred while saving the operator",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <WithActionBar actionBar={<ActionBar />}>
      <Content title={"Edit operator - " + operator.name}>
        <Card>
          {operator && <EditOperatorForm operator={operatorForm} locations={locations} onSubmit={putOperator} />}
        </Card>
      </Content>
    </WithActionBar>
  );
}

function ActionBar() {
  return (
    <div className="flex justify-between items-center">
      <BackButton />
      <ActionButton eventKey="operator:save" type="button">
        Save Operator <ChevronRight size={16} className="ml-2" />
      </ActionButton>
    </div>
  );
}

export function getOperatorData(id: string) {
  return fetch(`/api/operators/operator/${id}`);
}

export const queryClientOperator = new QueryClient();

export function operatorLoader({ params: { id } }: { params: any }) {
  return queryClientOperator.fetchQuery({
    queryKey: ["operator", id],
    queryFn: () => getOperatorData(id),
  });
}

function makeBoardingInfo(
  locations: YachtLocation[],
  boardingInfos: BoardingInfo[],
): OperatorForm["boardingInformations"] {
  return locations.map((loc) => {
    const info = boardingInfos.find((bi) => bi.location.id === loc.id);

    if (info) {
      return {
        description: info.description,
        file: info.file,
        locationId: loc.id,
      };
    }
    return {
      description: "",
      file: null,
      locationId: loc.id,
    };
  });
}
